import React, { useState, useContext } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import makeStyles from '@material-ui/core/styles/makeStyles';
import SourceContext from 'helpers/SourceContext';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import _get from 'lodash/get';
import { useSnackbar } from 'notistack';
import classNames from 'classnames';
import { fetchSignedUpload, createSourceFile, fetchSourceFileHeadings } from 'store/sources';
import makeGetSource from 'fetches/makeGetSource';
import useSource from 'pages/Directory/helpers/useSource';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';
import { saveSource } from 'store/sources';

// Components
import CardHeader from '@material-ui/core/CardHeader';
import Link from '@material-ui/core/Link';

import FileDrop from 'components/FileDrop';
import Base from 'pages/Directory/Base';
import FileUploadInfoCard from './FileUploadInfoCard';
import { Prompt } from 'react-router';

import sourceData from 'pages/Directory/sourceData';
import { MAP_ATTRIBUTES_URL } from 'pages/Directory/consts';

function UploadCsv() {
  const analytics = useAnalytics();
  const classes = useStyles() as any;
  const sourceContext = useContext(SourceContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { sourceInfo, setSourceInfo } = sourceContext;
  const { directoryId, sourceId, isPrimary } = sourceInfo;
  const getSource = makeGetSource(sourceId);
  const csvSourceData = sourceData['csv'];
  const [file, setFile] = useState() as any;
  const [waiting, setWaiting] = useState(true);
  const [disableOptOut, setDisableOptOut] = useState(false);
  const [errorText, setErrorText] = useState('');
  const source = useSource(sourceId);

  const disableNextButton =
    waiting ||
    ((!file || !!errorText) && !_get(sourceInfo, ['source', 'setupProgress', 'csvOptOut'], false));
  const baseUrl = makeSourceCreateBaseUrl(directoryId, sourceId);

  const handleFileChange = (file: any) => {
    if (!file) return;

    const extension = file.name
      .toLowerCase()
      .split('.')
      .pop();

    setFile(file);
    if (extension === 'csv' || extension === 'txt') {
      setErrorText('');
      analytics.track('Wizard - CSV Upload Success', {
        category: 'AD - Directories',
      });
    } else {
      setErrorText('Only CSV files or plain text files in CSV format are accepted.');
      analytics.track('Wizard - CSV Upload Error', {
        category: 'AD - Directories',
      });
    }

    setWaiting(false);
  };

  const handleNext = async () => {
    setWaiting(true);
    setDisableOptOut(true);
    enqueueSnackbar('Uploading file...');

    const signedUploadResponse = await dispatch(
      fetchSignedUpload({
        name: file.name,
        contentType: 'text/csv',
        size: file.size,
        sourceId,
      }),
    );

    const signedUpload = _get(signedUploadResponse, 'body');

    await fetch(signedUpload.url, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        ...signedUpload.fields,
        Accept: `${signedUpload.fields['Content-Type']}`,
      },
      body: file,
    });

    await dispatch(
      createSourceFile({
        sourceId,
        key: signedUpload.key,
        size: file.size,
        contentType: 'text/csv',
        name: file.name,
      }),
    );

    if (_get(sourceInfo, ['source', 'setupProgress', 'csvOptOut']) === true) {
      const updatedSource = {
        ...source,
        setupProgress: {
          ..._get(sourceInfo, ['source', 'setupProgress'], {}),
          csvOptOut: false,
        },
      };

      await dispatch(saveSource(updatedSource));
      await dispatch(dispatch(getSource()));
    }

    setTimeout(() => {
      history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
    }, 4000);
  };

  const handleOptOutCsvUpload = async () => {
    setWaiting(true);
    setDisableOptOut(true);
    enqueueSnackbar('Saving your preferences...');

    const updatedSource = {
      ...source,
      setupProgress: {
        ..._get(sourceInfo, ['source', 'setupProgress'], {}),
        csvOptOut: true,
      },
    };
    await dispatch(saveSource(updatedSource));
    await dispatch(getSource());

    analytics.track('Wizard - Wizard - CSV Upload Skipped', {
      category: 'AD - Directories',
    });

    setTimeout(() => {
      history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
    }, 4000);
  };

  return (
    <Base
      onNext={handleNext}
      currentStep={1}
      disableNextButton={disableNextButton}
      buttonText="Map your data"
      footer={
        <span>
          If you plan on using SFTP or don’t have your file ready, you can{' '}
          <Link
            onClick={!disableOptOut ? handleOptOutCsvUpload : () => {}}
            className={classNames(classes.link, { [classes.disabled]: disableOptOut })}
            color="secondary"
          >
            upload later
          </Link>
          .
        </span>
      }
    >
      <CardHeader title={'Upload a CSV'} subheader={csvSourceData.uploadCopy} />
      <>
        <div className={classes.fileDropContainer}>
          <FileUploadInfoCard isPrimary={isPrimary} />
          <FileDrop
            data-testid="csv-file-drop"
            hasError={!!errorText}
            onFileChange={handleFileChange}
            errorText={errorText}
            viewClassName={classes.fileDropRoot}
            aria-label="csv-file-drop"
          />
        </div>
      </>
    </Base>
  );
}

const useStyles = makeStyles((theme: any) => {
  return {
    fileDropContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    fileDropRoot: {
      ...theme.typography.h6,
      padding: 0,
      justifyContent: 'center',
      height: 208,
    },
    link: {
      cursor: 'pointer',
    },
    disabled: {
      color: theme.palette.action.disabled,
      cursor: 'not-allowed',
    },
  };
});

export default UploadCsv;
